// ─────────────────────────────────────────────────────────────────────────────
// visualizationTemplates.ts
// Starter templates for the visualization form in Admin Console.
// These are pre-populated in the Data and Additional Config tabs when a new
// visualization is created. Each template includes all required parameters
// for that chart type so new users understand the full structure.
//
// Templates are keyed by chartType (lowercase) to match CHART_TYPE constants.
// ─────────────────────────────────────────────────────────────────────────────

// ── Query templates (Data tab) ────────────────────────────────────────────────
// aggrQuery varies per chart type:
//   metric       → cardinality (single count)
//   line         → date_histogram (trend over time)
//   bar /
//   horizontalbar → terms (top-N by count)
//   table        → terms with sub-aggregation (breakdown list)
//   pie          → filters (named category buckets)

export const QUERY_TEMPLATES: Record<string, string> = {
    metric: JSON.stringify(
        [
            {
                module: "COMMON",
                dataSource: "ES",
                requestQueryMap:
                    '{"District" : "district", "Facility Type": "orgType", "Block" : "block"}',
                dateRefField: "registrationDateTimestamp",
                indexName: "dev-aastrika-user-registration-data",
                esInstance: "QUADNARY",
                filterPath: "",
                aggrQuery:
                    '{"size":0,"aggs":{"filtered_data":{"filter":{"bool":{"filter":[{"terms":{"profession":["ANM-Bihar"]}}]}},"aggs":{"YOUR_AGGREGATION_NAME":{"cardinality":{"field":"userId"}}}}}}',
            },
        ],
        null,
        2
    ),

    line: JSON.stringify(
        [
            {
                module: "COMMON",
                dataSource: "ES",
                requestQueryMap:
                    '{"District" : "district", "Facility Type": "orgType", "Block" : "block"}',
                dateRefField: "registrationDateTimestamp",
                indexName: "dev-aastrika-user-registration-data",
                esInstance: "QUADNARY",
                filterPath: "",
                aggrQuery:
                    '{"size":0,"aggs":{"filtered_data":{"filter":{"bool":{"filter":[{"terms":{"profession":["ANM-Bihar"]}}]}},"aggs":{"YOUR_TREND_NAME":{"date_histogram":{"field":"registrationDateTimestamp","calendar_interval":"day","format":"yyyy-MM-dd","min_doc_count":1},"aggs":{"unique_users":{"cardinality":{"field":"userId"}}}}}}}}',
            },
        ],
        null,
        2
    ),

    bar: JSON.stringify(
        [
            {
                module: "COMMON",
                dataSource: "ES",
                requestQueryMap:
                    '{"District" : "district", "Facility Type": "orgType", "Block" : "block"}',
                dateRefField: "registrationDateTimestamp",
                indexName: "dev-aastrika-user-registration-data",
                esInstance: "QUADNARY",
                filterPath: "aggregations.filtered_data.YOUR_GROUP_NAME.buckets",
                aggrQuery:
                    '{"size":0,"aggs":{"filtered_data":{"filter":{"bool":{"filter":[{"terms":{"profession":["ANM-Bihar"]}}]}},"aggs":{"YOUR_GROUP_NAME":{"terms":{"field":"district","size":10,"order":{"unique_users":"desc"}},"aggs":{"unique_users":{"cardinality":{"field":"userId"}}}}}}}}',
            },
        ],
        null,
        2
    ),

    horizontalbar: JSON.stringify(
        [
            {
                module: "COMMON",
                dataSource: "ES",
                requestQueryMap:
                    '{"District" : "district", "Facility Type": "orgType", "Block" : "block"}',
                dateRefField: "registrationDateTimestamp",
                indexName: "dev-aastrika-user-registration-data",
                esInstance: "QUADNARY",
                filterPath: "aggregations.filtered_data.YOUR_GROUP_NAME.buckets",
                aggrQuery:
                    '{"size":0,"aggs":{"filtered_data":{"filter":{"bool":{"filter":[{"terms":{"profession":["ANM-Bihar"]}}]}},"aggs":{"YOUR_GROUP_NAME":{"terms":{"field":"district","size":10,"order":{"unique_users":"desc"}},"aggs":{"unique_users":{"cardinality":{"field":"userId"}}}}}}}}',
            },
        ],
        null,
        2
    ),

    table: JSON.stringify(
        [
            {
                module: "COMMON",
                dataSource: "ES",
                requestQueryMap:
                    '{"District" : "district", "Facility Type": "orgType", "Block" : "block"}',
                dateRefField: "passbookEffectiveDateTimestamp",
                indexName: "dev-aastrika-enriched-user-competency-passbook",
                esInstance: "QUADNARY",
                filterPath: "aggregations.filtered_data.YOUR_BREAKDOWN_NAME.buckets",
                aggrQuery:
                    '{"size":0,"aggs":{"filtered_data":{"filter":{"bool":{"filter":[{"terms":{"profession":["ANM-Bihar"]}}]}},"aggs":{"YOUR_BREAKDOWN_NAME":{"terms":{"field":"competencyName.keyword","size":100},"aggs":{"YOUR_COUNT_NAME":{"cardinality":{"field":"userId"}}}}}}}}',
            },
        ],
        null,
        2
    ),

    pie: JSON.stringify(
        [
            {
                module: "COMMON",
                dataSource: "ES",
                requestQueryMap:
                    '{"District" : "district", "Facility Type": "orgType", "Block" : "block"}',
                dateRefField: "registrationDateTimestamp",
                indexName: "dev-aastrika-user-registration-data",
                esInstance: "QUADNARY",
                filterPath: "aggregations.filtered_data.YOUR_CATEGORY_NAME.buckets",
                aggrQuery:
                    '{"size":0,"aggs":{"filtered_data":{"filter":{"bool":{"filter":[{"terms":{"profession":["ANM-Bihar"]}}]}},"aggs":{"YOUR_CATEGORY_NAME":{"filters":{"filters":{"Category A":{"term":{"orgType":"Government"}},"Category B":{"term":{"orgType":"Private Sector"}}}},"aggs":{"user_count":{"cardinality":{"field":"userId"}}}}}}}}',
            },
        ],
        null,
        2
    ),
};

// Fallback for chart types without a specific template
export const DEFAULT_QUERY_TEMPLATE = JSON.stringify(
    [
        {
            module: "COMMON",
            dataSource: "ES",
            requestQueryMap:
                '{"District" : "district", "Facility Type": "orgType", "Block" : "block"}',
            dateRefField: "registrationDateTimestamp",
            indexName: "dev-aastrika-user-registration-data",
            esInstance: "QUADNARY",
            filterPath: "",
            aggrQuery:
                '{"size":0,"aggs":{"filtered_data":{"filter":{"bool":{"filter":[{"terms":{"profession":["ANM-Bihar"]}}]}},"aggs":{"YOUR_AGGREGATION_NAME":{"cardinality":{"field":"userId"}}}}}}',
        },
    ],
    null,
    2
);

// ── Additional Config templates (Additional Config tab) ───────────────────────
// aggregationPaths must match the aggregation name used in aggrQuery above.
// Replace YOUR_AGGREGATION_NAME / YOUR_TREND_NAME / YOUR_GROUP_NAME etc.
// with the actual name you used in the aggrQuery.

export const CONFIG_TEMPLATES: Record<string, string> = {
    metric: JSON.stringify(
        {
            chartType: "metric",
            valueType: "number",
            isDecimal: false,
            action: "COUNT_OF_RECORDS",
            filter: "",
            documentType: "_doc",
            drillChart: "none",
            resultType: "value",
            aggregationPaths: ["YOUR_AGGREGATION_NAME"],
            insight: {},
            _comment: "Describe what this visualization shows",
        },
        null,
        2
    ),

    line: JSON.stringify(
        {
            chartType: "line",
            valueType: "number",
            isDecimal: false,
            action: "",
            filter: "",
            documentType: "_doc",
            drillChart: "none",
            resultType: "value",
            aggregationPaths: ["YOUR_TREND_NAME"],
            isCumulative: false,
            interval: "day",
            insight: {},
            _comment: "Describe what this trend visualization shows",
        },
        null,
        2
    ),

    bar: JSON.stringify(
        {
            chartType: "bar",
            valueType: "number",
            isDecimal: false,
            action: "",
            filter: "",
            documentType: "_doc",
            drillChart: "none",
            resultType: "value",
            aggregationPaths: ["YOUR_GROUP_NAME"],
            insight: {},
            _comment: "Describe what this bar chart shows",
        },
        null,
        2
    ),

    horizontalbar: JSON.stringify(
        {
            chartType: "horizontalBar",
            valueType: "number",
            isDecimal: false,
            action: "",
            filter: "",
            documentType: "_doc",
            drillChart: "none",
            resultType: "value",
            aggregationPaths: ["YOUR_GROUP_NAME"],
            insight: {},
            _comment: "Describe what this horizontal bar chart shows",
        },
        null,
        2
    ),

    table: JSON.stringify(
        {
            chartType: "table",
            valueType: "number",
            isDecimal: false,
            action: "",
            filter: "",
            documentType: "_doc",
            drillChart: "none",
            resultType: "value",
            aggregationPaths: ["YOUR_BREAKDOWN_NAME"],
            insight: {},
            _comment: "Describe what this table shows",
        },
        null,
        2
    ),

    pie: JSON.stringify(
        {
            chartType: "pie",
            valueType: "number",
            isDecimal: false,
            action: "",
            filter: "",
            documentType: "_doc",
            drillChart: "none",
            resultType: "value",
            aggregationPaths: ["YOUR_CATEGORY_NAME"],
            insight: {},
            _comment: "Describe what this pie chart shows",
        },
        null,
        2
    ),
};

// Fallback config for chart types without a specific template
export const DEFAULT_CONFIG_TEMPLATE = JSON.stringify(
    {
        chartType: "metric",
        valueType: "number",
        isDecimal: false,
        action: "COUNT_OF_RECORDS",
        filter: "",
        documentType: "_doc",
        drillChart: "none",
        resultType: "value",
        aggregationPaths: ["YOUR_AGGREGATION_NAME"],
        insight: {},
        _comment: "Describe what this visualization shows",
    },
    null,
    2
);